import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

const TileContent = ({ tile }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 52,
        height: 54,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: COLORS.primary.light,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={tile?.imageSrc || `/source-${tile?.type}.png`}
        alt={tile?.title || 'source'}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 48,
          height: 48,
          objectFit: 'contain',
          filter: tile?.disabled ? 'grayscale(1)' : 'none',
        }}
        onError={e => {
          e.target.style.display = 'none';
        }}
      />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: FONT_SIZES.body1,
          fontWeight: FONT_WEIGHTS.medium,
          color: tile?.disabled ? COLORS.text.secondary : COLORS.text.primary,
          mb: tile?.description ? 0.5 : 0,
        }}
      >
        {tile?.title}
      </Typography>
      {tile?.description && (
        <Typography
          sx={{
            fontSize: FONT_SIZES.caption,
            color: COLORS.text.secondary,
          }}
        >
          {tile?.description}
        </Typography>
      )}
    </Box>
  </Box>
);

TileContent.propTypes = {
  tile: PropTypes.object.isRequired,
};

/**
 * MUI TileList: selectable list of tiles (e.g. connector sources).
 * Replaces Carbon RadioTile / TileGroup. Uses Grid for layout (max 3 per row, no horizontal scroll).
 */
const TileList = ({ title, tileList, handleSelection = () => {} }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${COLORS.border.light}`,
        mb: 3,
        overflow: 'hidden',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1.5,
          bgcolor: COLORS.primary.light,
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: FONT_SIZES.h5,
            fontWeight: FONT_WEIGHTS.semiBold,
            color: COLORS.text.primary,
          }}
        >
          {title}
        </Typography>
        <Box
          component="span"
          sx={{
            width: 24,
            height: 24,
            ml: 1.5,
            borderRadius: '50%',
            bgcolor: COLORS.primary.main,
            color: COLORS.primary.contrastText,
            fontSize: FONT_SIZES.caption,
            fontWeight: FONT_WEIGHTS.medium,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {tileList?.length ?? 0}
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ p: 2, pt: 1.5, pb: 1 }}>
        {tileList?.map(tile => {
          const isDisabled = Boolean(tile?.disabled);

          const card = (
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: `1px solid ${COLORS.border.light}`,
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'border-color 0.2s, background-color 0.2s',
                bgcolor: isDisabled
                  ? COLORS.background.default
                  : COLORS.background.paper,
                opacity: isDisabled ? 0.72 : 1,
                ...(!isDisabled && {
                  '&:hover': {
                    borderColor: COLORS.primary.main,
                    bgcolor: COLORS.primary.light,
                  },
                }),
              }}
            >
              {isDisabled ? (
                <Box
                  sx={{
                    p: 2,
                    display: 'block',
                    textAlign: 'left',
                    height: '100%',
                    cursor: 'not-allowed',
                  }}
                >
                  <TileContent tile={tile} />
                </Box>
              ) : (
                <CardActionArea
                  onClick={() => handleSelection(tile?.id)}
                  sx={{ p: 2, display: 'block', textAlign: 'left', height: '100%' }}
                >
                  <TileContent tile={tile} />
                </CardActionArea>
              )}
            </Card>
          );

          return (
            <Grid item xs={12} sm={6} md={4} key={tile?.id}>
              {isDisabled ? (
                <Tooltip title={tile?.disabledTooltip || ''} placement="top" arrow>
                  <Box sx={{ height: '100%' }}>{card}</Box>
                </Tooltip>
              ) : (
                card
              )}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

TileList.propTypes = {
  title: PropTypes.string.isRequired,
  tileList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      imageSrc: PropTypes.string,
      disabled: PropTypes.bool,
      disabledTooltip: PropTypes.string,
    })
  ),
  handleSelection: PropTypes.func,
};

export default TileList;
